/** Cross-module teacher “story” — powered by GET /Teacher/detail + leave. */

import api from "services/api";
import { fetchTeacherDetail } from "services/teacherDetailService";
import { apiurl } from "utils";
import { parseListPayload } from "utils/paginationStats";
import { findTeacherById, resolveTeacherQuery } from "assistant/apiHandlers";
import { resolveProfilePic } from "utils/teacherAvatar";
import { readIsLicensed, readIsTeacher } from "utils/teacherFlags";

const withSlash = (url = "") => (url.endsWith("/") ? url : `${url}/`);
const leaveApiBase = withSlash(process.env.REACT_APP_LEAVE_API_BASE || apiurl);
const LIST_PAGE_SIZE = 500;

function authHeaders() {
  const token = localStorage.getItem("tk446655440000");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function normalizeSearch(text) {
  return String(text ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
}

function namesMatch(a, b) {
  const na = normalizeSearch(a);
  const nb = normalizeSearch(b);
  if (!na || !nb) return false;
  return na.includes(nb) || nb.includes(na);
}

function mapDetailProfile(profile = {}) {
  const name =
    profile.fullName ||
    [profile.name, profile.preName].filter(Boolean).join(" ") ||
    "—";
  return {
    id: profile.id,
    name,
    reg: profile.regNumber ?? "—",
    phone: profile.phone ?? "—",
    status: profile.status ?? "",
    recruiter: profile.recruiter ?? "",
    profilePic: resolveProfilePic(profile),
    hasProfilePic: profile.hasProfilePic ?? profile.HasProfilePic,
    score: profile.score ?? null,
    degree: profile.degree ?? "",
    isLicensed: readIsLicensed(profile.isLicensed),
    isTeacher: readIsTeacher(profile.isTeacher),
    isAllocated: Boolean(profile.isAllocated),
    hasAttendance: Boolean(profile.hasAttendance),
    email: profile.email ?? "",
    whatsapp: profile.whatsapp ?? "",
    gender: profile.gender ?? "",
    lastScoreDate: profile.lastScoreDate ?? null,
  };
}

async function fetchLeavesForTeacher(teacher) {
  const { data } = await api.get(`${leaveApiBase}LeaveRequests/list`, {
    params: { pageNumber: 1, pageSize: LIST_PAGE_SIZE },
    headers: authHeaders(),
  });
  const rows = parseListPayload(data);
  return rows.filter((row) => {
    const tid = row.teacherId ?? row.TeacherId;
    if (tid && teacher.id && String(tid) === String(teacher.id)) return true;
    return namesMatch(row.teacherName ?? row.TeacherName ?? "", teacher.name);
  });
}

function pickActiveLeave(leaves) {
  const pending = leaves.filter((l) => String(l.status ?? "").toLowerCase() === "pending");
  const approved = leaves.filter((l) => String(l.status ?? "").toLowerCase() === "approved");
  return { pending, approved, recent: leaves.slice(0, 3) };
}

function formatPct(value) {
  if (value == null || Number.isNaN(Number(value))) return null;
  return `${Number(value).toFixed(1)}%`;
}

function buildNarrative(story, language) {
  const fr = language !== "en";
  const t = story.teacher;
  const s = story.summary || {};
  const ranking = story.ranking || {};
  const assignment = story.currentAssignment;
  const parts = [];

  const licenseLabel = t.isLicensed
    ? fr
      ? "licenciée"
      : "licensed"
    : fr
      ? "non licenciée"
      : "unlicensed";
  const scoreBit = t.score != null ? ` · score **${t.score}**` : "";
  parts.push(`**${t.name}** (${t.reg}) — ${licenseLabel}${scoreBit}`);

  if (assignment?.isAssigned || assignment?.estabName) {
    const loc = [assignment.estabName, assignment.locationLabel || assignment.provinceName]
      .filter(Boolean)
      .join(" · ");
    parts.push(fr ? `Affectée à **${loc || "—"}**.` : `Assigned to **${loc || "—"}**.`);
  } else {
    parts.push(fr ? "Aucune affectation active." : "No active assignment.");
  }

  const { pending, approved } = story.leaveSummary;
  if (pending.length) {
    parts.push(fr ? `**${pending.length}** demande(s) de congé en attente.` : `**${pending.length}** pending leave request(s).`);
  } else if (approved.length) {
    parts.push(fr ? `Congé approuvé récent — statut actif possible.` : `Recent approved leave — may be active.`);
  } else if (story.leaves.length) {
    parts.push(fr ? `${story.leaves.length} demande(s) de congé archivée(s).` : `${story.leaves.length} leave request(s) on file.`);
  } else {
    parts.push(fr ? "Aucune demande de congé." : "No leave requests.");
  }

  if (s.totalAttendance != null || s.thisMonth != null) {
    const radiusPct = formatPct(s.attendanceWithinRadiusPercent);
    const daysSince =
      s.daysSinceLastAttendance != null
        ? fr
          ? `dernier pointage il y a **${s.daysSinceLastAttendance}** j`
          : `last check-in **${s.daysSinceLastAttendance}** day(s) ago`
        : null;
    parts.push(
      fr
        ? `Présence : **${s.totalAttendance ?? 0}** pointage(s) · **${s.thisMonth ?? 0}** ce mois · **${s.thisWeek ?? 0}** cette semaine${
            radiusPct ? ` · **${radiusPct}** dans le rayon` : ""
          }${daysSince ? ` · ${daysSince}` : ""}.`
        : `Attendance: **${s.totalAttendance ?? 0}** check-in(s) · **${s.thisMonth ?? 0}** this month · **${s.thisWeek ?? 0}** this week${
            radiusPct ? ` · **${radiusPct}** in radius` : ""
          }${daysSince ? ` · ${daysSince}` : ""}.`
    );
  }

  if (ranking.overallRank != null || ranking.provinceRank != null) {
    const bits = [];
    if (ranking.overallRank != null) {
      bits.push(fr ? `rang global **#${ranking.overallRank}**` : `overall rank **#${ranking.overallRank}**`);
    }
    if (ranking.provinceRank != null) {
      bits.push(
        fr
          ? `province **#${ranking.provinceRank}**${ranking.provinceName ? ` (${ranking.provinceName})` : ""}`
          : `province **#${ranking.provinceRank}**${ranking.provinceName ? ` (${ranking.provinceName})` : ""}`
      );
    }
    if (ranking.departmentRank != null) {
      bits.push(
        fr
          ? `département **#${ranking.departmentRank}**${ranking.departmentName ? ` (${ranking.departmentName})` : ""}`
          : `department **#${ranking.departmentRank}**${ranking.departmentName ? ` (${ranking.departmentName})` : ""}`
      );
    }
    if (bits.length) parts.push(fr ? `Classement : ${bits.join(" · ")}.` : `Ranking: ${bits.join(" · ")}.`);
  }

  if (story.recentAttendance?.length) {
    const last = story.recentAttendance[0];
    const when = String(last.date ?? "").slice(0, 16).replace("T", " ");
    parts.push(
      fr
        ? `Dernier pointage : **${when || "—"}** · ${last.estabName ?? "—"}${
            last.isWithinRadius === false ? " (hors rayon)" : ""
          }.`
        : `Latest check-in: **${when || "—"}** · ${last.estabName ?? "—"}${
            last.isWithinRadius === false ? " (outside radius)" : ""
          }.`
    );
  }

  return parts.join("\n\n");
}

function buildStructuredText(story) {
  const t = story.teacher;
  const s = story.summary || {};
  const ranking = story.ranking || {};
  const a = story.currentAssignment;
  const lines = [
    `Teacher: ${t.name} (${t.reg}), status ${t.status || "—"}, licensed ${t.isLicensed ? "yes" : "no"}, score ${t.score ?? "—"}`,
  ];
  if (a?.estabName) {
    lines.push(`Assignment: ${a.estabName} (${a.provinceName || a.locationLabel || "—"})`);
  } else {
    lines.push("Assignment: none");
  }
  lines.push(
    `Leave: ${story.leaves.length} total, ${story.leaveSummary.pending.length} pending, ${story.leaveSummary.approved.length} approved`
  );
  lines.push(
    `Attendance summary: total ${s.totalAttendance ?? 0}, this month ${s.thisMonth ?? 0}, this week ${s.thisWeek ?? 0}, radius ${s.attendanceWithinRadiusPercent ?? "—"}%, days since last ${s.daysSinceLastAttendance ?? "—"}`
  );
  if (ranking.overallRank != null || ranking.provinceRank != null) {
    lines.push(
      `Ranking: overall #${ranking.overallRank ?? "—"}, province #${ranking.provinceRank ?? "—"}, department #${ranking.departmentRank ?? "—"}`
    );
  }
  if (story.recentAttendance?.length) {
    const last = story.recentAttendance[0];
    lines.push(`Latest check-in: ${String(last.date ?? "").slice(0, 16)} at ${last.estabName ?? "—"}`);
  }
  return lines.join(". ");
}

function buildTimeline(story, language) {
  const fr = language !== "en";
  const t = story.teacher;
  const s = story.summary || {};
  const ranking = story.ranking || {};
  const items = [
    {
      id: "profile",
      title: fr ? "Profil" : "Profile",
      detail: [
        t.name,
        t.reg,
        t.status,
        t.isLicensed ? (fr ? "licenciée" : "licensed") : fr ? "non licenciée" : "unlicensed",
        t.score != null ? `score ${t.score}` : null,
      ]
        .filter(Boolean)
        .join(" · "),
    },
  ];

  const a = story.currentAssignment;
  if (a?.isAssigned || a?.estabName) {
    items.push({
      id: "assignment",
      title: fr ? "Affectation" : "Assignment",
      detail: [a.estabName, a.locationLabel || a.provinceName, a.assignedDate ? String(a.assignedDate).slice(0, 10) : null]
        .filter(Boolean)
        .join(" · "),
    });
  } else {
    items.push({ id: "assignment", title: fr ? "Affectation" : "Assignment", detail: fr ? "Non affectée" : "Not assigned" });
  }

  const active = story.leaveSummary.pending[0] || story.leaveSummary.approved[0] || story.leaves[0];
  if (active) {
    items.push({
      id: "leave",
      title: fr ? "Congé" : "Leave",
      detail: `${active.leaveType ?? "—"} · ${active.status ?? "—"} · ${String(active.startDate ?? "").slice(0, 10)} → ${String(active.endDate ?? "").slice(0, 10)}`,
    });
  } else {
    items.push({ id: "leave", title: fr ? "Congé" : "Leave", detail: fr ? "Aucune demande" : "No requests" });
  }

  items.push({
    id: "attendance",
    title: fr ? "Présence" : "Attendance",
    detail: [
      s.totalAttendance != null ? `${s.totalAttendance} ${fr ? "pointages" : "check-ins"}` : null,
      s.thisMonth != null ? `${s.thisMonth} ${fr ? "ce mois" : "this month"}` : null,
      s.attendanceWithinRadiusPercent != null
        ? `${formatPct(s.attendanceWithinRadiusPercent)} ${fr ? "dans rayon" : "in radius"}`
        : null,
      s.daysSinceLastAttendance != null
        ? fr
          ? `dernier il y a ${s.daysSinceLastAttendance} j`
          : `last ${s.daysSinceLastAttendance}d ago`
        : null,
    ]
      .filter(Boolean)
      .join(" · ") || (fr ? "Pas de données" : "No data"),
  });

  if (ranking.overallRank != null || ranking.provinceRank != null) {
    items.push({
      id: "ranking",
      title: fr ? "Classement" : "Ranking",
      detail: [
        ranking.overallRank != null ? `#${ranking.overallRank} ${fr ? "global" : "overall"}` : null,
        ranking.provinceRank != null ? `#${ranking.provinceRank} ${ranking.provinceName || (fr ? "province" : "province")}` : null,
        ranking.departmentRank != null
          ? `#${ranking.departmentRank} ${ranking.departmentName || (fr ? "département" : "department")}`
          : null,
      ]
        .filter(Boolean)
        .join(" · "),
    });
  }

  if (story.recentAttendance?.length) {
    const last = story.recentAttendance[0];
    items.push({
      id: "history",
      title: fr ? "Dernier pointage" : "Latest check-in",
      detail: `${String(last.date ?? "").slice(0, 16).replace("T", " ")} · ${last.estabName ?? "—"}`,
    });
  }

  return items;
}

function buildStoryFromDetail(detail, leaves, language) {
  const teacher = mapDetailProfile(detail?.profile);
  const leaveSummary = pickActiveLeave(leaves);
  const story = {
    teacher,
    detail,
    summary: detail?.summary || {},
    ranking: detail?.ranking || {},
    currentAssignment: detail?.currentAssignment || null,
    recentAttendance: Array.isArray(detail?.recentAttendance) ? detail.recentAttendance : [],
    charts: detail?.charts || null,
    assignments: detail?.currentAssignment?.estabName
      ? [
          {
            estName: detail.currentAssignment.estabName,
            provinceName: detail.currentAssignment.provinceName,
            assigDateTime: detail.currentAssignment.assignedDate,
          },
        ]
      : [],
    leaves,
    leaveSummary,
  };

  return {
    ok: true,
    story,
    narrative: buildNarrative(story, language),
    structuredText: buildStructuredText(story),
    timeline: buildTimeline(story, language),
  };
}

export async function fetchTeacherStory(query, { teacherId, pageContext, language } = {}) {
  let teacher = null;

  try {
    if (teacherId) {
      teacher = await findTeacherById(teacherId);
      if (!teacher) {
        teacher = { id: teacherId, name: `Teacher #${teacherId}`, reg: "—", phone: "—", status: "", recruiter: "" };
      }
    }

    if (!teacher && query) {
      const resolved = await resolveTeacherQuery(query, 8);
      if (resolved.status === "found") {
        teacher = resolved.teacher;
      } else if (resolved.status === "ambiguous") {
        return { ok: false, reason: "ambiguous", query: resolved.query, matches: resolved.matches };
      } else if (resolved.status === "not_found") {
        return { ok: false, reason: "not_found", query: resolved.query || query };
      }
    }

    if (!teacher) {
      return { ok: false, reason: "missing_query" };
    }

    const detailOpts = {};
    if (teacher.id) detailOpts.id = teacher.id;
    else if (teacher.reg && teacher.reg !== "—") detailOpts.regNumber = teacher.reg;
    detailOpts.historyLimit = 12;
    detailOpts.chartMonths = 6;

    const [detailSettled, leavesSettled] = await Promise.allSettled([
      fetchTeacherDetail(detailOpts),
      fetchLeavesForTeacher(teacher),
    ]);

    const leaves = leavesSettled.status === "fulfilled" ? leavesSettled.value : [];

    if (detailSettled.status === "fulfilled" && detailSettled.value?.profile) {
      return buildStoryFromDetail(detailSettled.value, leaves, language);
    }

    // Fallback: list row + leave only (detail API unavailable)
    const mergedTeacher = {
      ...teacher,
      profilePic: teacher.profilePic || resolveProfilePic(teacher),
    };
    const leaveSummary = pickActiveLeave(leaves);
    const story = {
      teacher: mergedTeacher,
      summary: {},
      ranking: {},
      currentAssignment: null,
      recentAttendance: [],
      charts: null,
      assignments: [],
      leaves,
      leaveSummary,
    };

    return {
      ok: true,
      story,
      narrative: buildNarrative(story, language),
      structuredText: buildStructuredText(story),
      timeline: buildTimeline(story, language),
      detailUnavailable: true,
    };
  } catch (err) {
    console.error("[Assistant] fetchTeacherStory failed", err);
    return { ok: false, reason: "error", query, error: err?.message || String(err) };
  }
}
